import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import { PaginationDefaults } from '@terra/shared-dto-types';
import { fetchFindingsListEnrichedPageForTarget } from './target-findings-api';

export const MENTIONS_FINDINGS_QUERY_KEY = 'mentions-findings';

const MENTIONS_FINDINGS_PAGE_SIZE = PaginationDefaults.LIMIT;

type MentionsFindingsPage = {
  findings: Awaited<
    ReturnType<typeof fetchFindingsListEnrichedPageForTarget>
  >['findings'];
  total: number;
};

export function useMentionsFindings({
  targetId,
  targetType,
  searchTerm,
  enabled = true,
}: {
  targetId: string | null | undefined;
  targetType: TargetTypeType;
  searchTerm: string;
  enabled?: boolean;
}) {
  const trimmedSearch = searchTerm.trim();

  const query = useInfiniteQuery({
    queryKey: [
      MENTIONS_FINDINGS_QUERY_KEY,
      targetId,
      targetType,
      trimmedSearch,
    ],
    queryFn: async ({ pageParam = 0 }): Promise<MentionsFindingsPage> => {
      if (!targetId) {
        return { findings: [], total: 0 };
      }

      const skip = pageParam * MENTIONS_FINDINGS_PAGE_SIZE;

      return fetchFindingsListEnrichedPageForTarget({
        targetId,
        targetType,
        options: {
          skip,
          limit: MENTIONS_FINDINGS_PAGE_SIZE,
          searchTerm: trimmedSearch || undefined,
          sortBy: 'createdAt',
          sortOrder: 'desc',
        },
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce(
        (count, page) => count + page.findings.length,
        0,
      );

      return loadedCount < lastPage.total ? allPages.length : undefined;
    },
    enabled: Boolean(targetId) && enabled,
    staleTime: 30_000,
  });

  const findings = useMemo(
    () => query.data?.pages.flatMap((page) => page.findings) ?? [],
    [query.data],
  );

  return {
    findings,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: query.fetchNextPage,
  };
}
